import { createApp } from 'vue';
import { createHead } from '@unhead/vue/client';
import { createI18n } from '@/app/modules/i18n';
import { createToaster, createConfirmDialog } from '@/ui-kit';
import { createRouter } from '@/app/core/router';
import { createStore } from '@/app/core/store';
import { createApiClient } from '@/app/core/api-client';
import { config } from '@/app/core/config';
import { useAccountStore } from '@/app/modules/account';
import { useUserStore } from '@/app/modules/user';
import { getRemoteDemoConfig } from '@/app/shared/sheets';
import App from '@/app/app.vue';
import '@/app/app.css';
import '@/ui-kit/styles/main.scss';
const router = createRouter();
const apiClient = createApiClient({
  baseUrl: config.apiUrl,
  keycloak: {
    url: config.keycloak.url,
    realm: config.keycloak.realm,
    clientId: config.keycloak.clientId,
    silentCheckSsoRedirectUri: config.keycloak.silentCheckSsoRedirectUri,
    logoutRedirectUri: `${window.location.origin}${config.baseUrl}`,
  },
});
const store = createStore();
const app = createApp(App)
  .use(store)
  .use(apiClient)
  .use(router)
  .use(createI18n())
  .use(createHead())
  .use(createToaster())
  .use(createConfirmDialog());
app.config.idPrefix = 'onebeat';
function computeInitials(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}
const demoConfig = getRemoteDemoConfig();
if (demoConfig?.profile) {
  const accountStore = useAccountStore(store);
  const userStore = useUserStore(store);
  const orgName = demoConfig.profile.organizationName?.trim();
  const userName = demoConfig.profile.userName?.trim();
  const initials = demoConfig.profile.organizationInitials?.trim();
  if (orgName) {
    accountStore.name = orgName;
  }
  const computedInitials = orgName ? computeInitials(orgName) : initials || '';
  accountStore.initials = computedInitials;
  if (userName) {
    userStore.name = userName;
  }
  const theme = demoConfig.profile.theme;
  if (theme && theme !== 'default') {
    const themeColors: Record<string, { bg: string; dark?: boolean }> = {
      pink: { bg: '#eee3e6' },
      purple: { bg: '#e4e0ec' },
      lavender: { bg: '#492651', dark: true },
      midnight: { bg: '#1a2332', dark: true },
      sage: { bg: '#d4ddd6' },
    };
    const themeData = themeColors[theme];
    if (themeData) {
      document.documentElement.style.setProperty('--ui2-background-tone-5', themeData.bg);
      if (themeData.dark) {
        document.documentElement.classList.add('theme-dark-sidebar');
      }
    }
  }
}
app.mount('#app');
